import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  RiAddLine,
  RiEdit2Line,
  RiDeleteBin6Line,
  RiSearchLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
} from "react-icons/ri";

import {
  getNonInventoryItems,
  deleteNonInventoryItem,
} from "../services/nonInventoryService";

import "./OutletsList.css";

const NonInventoryItemListLayer = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🔍 search
  const [search, setSearch] = useState("");

  // 📄 pagination (UI only)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  useEffect(() => {
    loadItems();
  }, []);

  /* LOAD ITEMS (UNCHANGED) */
  const loadItems = async () => {
    try {
      const res = await getNonInventoryItems();
      if (res?.status === "success") {
        setItems(res.data || []);
      } else {
        toast.error("Failed to load items");
      }
    } catch {
      toast.error("API Error");
    }
    setLoading(false);
  };

  /* DELETE (UNCHANGED) */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item?")) return;

    try {
      const res = await deleteNonInventoryItem(id);
      if (res?.status === "success") {
        toast.success("Item deleted");
        loadItems();
      }
    } catch {
      toast.error("API Error");
    }
  };

  /* 🔍 SEARCH (UI only) */
  const filteredData = useMemo(() => {
    return items.filter((i) =>
      i.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [items, search]);

  /* 📄 PAGINATION (UI only) */
  const effectivePageSize =
    pageSize === "all" ? filteredData.length : pageSize;

  const totalPages =
    effectivePageSize === 0
      ? 1
      : Math.ceil(filteredData.length / effectivePageSize);

  const paginatedData =
    pageSize === "all"
      ? filteredData
      : filteredData.slice(
          (currentPage - 1) * effectivePageSize,
          currentPage * effectivePageSize
        );

  return (
    <div className="ul-page">
      {/* HEADER */}
      <div className="ul-header">
        <h4>Non Inventory Items</h4>
        <Link to="/add-non-inventory-item" className="ul-add-btn">
          <RiAddLine /> Add Item
        </Link>
      </div>

      {/* FILTER BAR */}
      <div className="ul-filters">
        <div className="ul-search">
          <RiSearchLine />
          <input
            placeholder="Search item..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="ul-pagesize">
          <span>Show</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(
                e.target.value === "all"
                  ? "all"
                  : Number(e.target.value)
              );
              setCurrentPage(1);
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value="all">All</option>
          </select>
          <span>entries</span>
        </div>
      </div>

      {/* TABLE */}
      <div className="ul-table-wrapper">
        <table className="ul-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Description</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="text-center">
                  Loading...
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center text-muted">
                  No data found
                </td>
              </tr>
            ) : (
              paginatedData.map((i, index) => (
                <tr key={i.id}>
                  <td>
                    {pageSize === "all"
                      ? index + 1
                      : (currentPage - 1) * effectivePageSize + index + 1}
                  </td>
                  <td>{i.name}</td>
                  <td>{i.description || "-"}</td>
                  <td className="ul-actions">
                    <Link
                      to={`/edit-non-inventory-item/${i.id}`}
                      className="ul-btn edit"
                    >
                      <RiEdit2Line /> Edit
                    </Link>

                    <button
                      className="ul-btn delete"
                      onClick={() => handleDelete(i.id)}
                    >
                      <RiDeleteBin6Line /> Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      {pageSize !== "all" && totalPages > 1 && (
        <div className="ul-pagination">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            <RiArrowLeftSLine />
          </button>

          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              className={currentPage === i + 1 ? "active" : ""}
              onClick={() => setCurrentPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}

          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            <RiArrowRightSLine />
          </button>
        </div>
      )}
    </div>
  );
};

export default NonInventoryItemListLayer;
